// Cuenta corriente / estado de cuenta por cliente: movimientos con filtros por
// dia / semana / mes / rango elegido, saldo, registro de PAGO/DEVOLUCION e
// impresion del estado de cuenta (factura interna) con logo.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas, ddmmaaaa } from "../../lib/fecha";
import { iconoImpresora, iconoTacho } from "../iconos";
import logoUrl from "../../img/logo-torremolinos.jpeg";
import type { Cliente, CuentaMov, PreparaPedido, PreparaItem, Sesion } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

/** Una linea de un pedido, para el estado de cuenta con detalle. */
interface LineaDeCuenta {
  pedido_id: number;
  cantidad: number;
  subtotal: number;
  codigo: number;
  descripcion: string;
}

interface CuentaResp {
  cliente: { id: number; codigo: string; nombre: string };
  saldo: number;
  movimientos: CuentaMov[];
  /** Solo viene con ?detalle=1. Vacio en la carga normal. */
  lineas: LineaDeCuenta[];
}

export async function renderCuentas(contenedor: HTMLElement): Promise<void> {
  const hoy = hoyMontevideo();
  const inicioMes = hoy.slice(0, 8) + "01";
  let datos: CuentaResp | null = null;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Cuentas</h1>
      <p class="subtitulo">Estado de cuenta por cliente. Registra pagos, devoluciones y faltantes, e imprime la factura.</p>
    </div>

    <div class="no-print tarjeta mb-4 flex flex-wrap items-end gap-3">
      <label class="min-w-[16rem] flex-1"><span class="etiqueta">Cliente</span>
        <select id="cliente" class="campo"><option value="">Cargando...</option></select></label>
      <label><span class="etiqueta">Desde</span><input id="desde" type="date" class="campo" value="${inicioMes}" /></label>
      <label><span class="etiqueta">Hasta</span><input id="hasta" type="date" class="campo" value="${hoy}" /></label>
      <div class="flex gap-1">
        <button class="btn-sec text-sm" data-rango="dia">Dia</button>
        <button class="btn-sec text-sm" data-rango="semana">Semana</button>
        <button class="btn-sec text-sm" data-rango="mes">Mes</button>
        <button class="btn-sec text-sm" data-rango="todo">Todo</button>
      </div>
      <button id="detalle" type="button" class="btn-sec" disabled aria-pressed="false" title="Mostrar los articulos de cada compra">Con detalles</button>
      <button id="imprimir" class="btn-sec" disabled title="Imprime el estado de cuenta tal como se ve en pantalla">${iconoImpresora} Imprimir</button>
      <button id="habilitarAnul" class="btn-sec hidden" title="Muestra un boton para anular al lado de cada compra">${iconoTacho} Habilitar anulacion</button>
    </div>

    <div id="avisoAnul" class="no-print mb-3 hidden rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 text-sm text-red-800">
      <b>Modo anulacion activo.</b> Cada compra que venga de un pedido muestra su boton para anularlo.
      El pedido sale de la produccion y la compra deja de contar en el saldo, pero no se borra:
      queda tachada. Volve a tocar el boton para apagarlo.
    </div>

    <div id="salida"></div>

    <div class="no-print tarjeta mt-4">
      <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Registrar movimiento</h2>
      <form id="fmov" class="flex flex-wrap items-end gap-3">
        <label><span class="etiqueta">Tipo</span>
          <select id="m_tipo" class="campo"><option value="PAGO">Pago</option><option value="DEVOLUCION">Devolucion</option><option value="FALTANTE">Faltante</option></select></label>
        <label><span class="etiqueta">Monto</span><input id="m_monto" type="number" min="0" class="campo w-32" /></label>
        <label><span class="etiqueta">Fecha</span><input id="m_fecha" type="date" class="campo" value="${hoy}" /></label>
        <label class="min-w-[12rem] flex-1"><span class="etiqueta">Notas</span><input id="m_notas" class="campo" placeholder="Opcional" /></label>
        <button class="btn-primario">Registrar</button>
        <span id="m_err" class="self-center text-sm text-red-600"></span>
      </form>
      <p id="m_ayuda" class="mt-2 hidden text-sm text-masa-500">Elegi los articulos abajo: el monto se calcula solo con la lista del cliente.</p>
      <div id="picker" class="mt-3 hidden space-y-2"></div>
    </div>

    <dialog id="dlgAnularCta"></dialog>`;

  const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
  const inpDesde = contenedor.querySelector("#desde") as HTMLInputElement;
  const inpHasta = contenedor.querySelector("#hasta") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;
  const btnDetalle = contenedor.querySelector("#detalle") as HTMLButtonElement;
  const btnImprimir = contenedor.querySelector("#imprimir") as HTMLButtonElement;
  const btnHabAnul = contenedor.querySelector("#habilitarAnul") as HTMLButtonElement;
  const avisoAnul = contenedor.querySelector("#avisoAnul") as HTMLElement;
  const dlgAnul = contenedor.querySelector("#dlgAnularCta") as HTMLDialogElement;
  const mErr = contenedor.querySelector("#m_err") as HTMLElement;
  const selTipo = contenedor.querySelector("#m_tipo") as HTMLSelectElement;
  const inpMonto = contenedor.querySelector("#m_monto") as HTMLInputElement;
  const mAyuda = contenedor.querySelector("#m_ayuda") as HTMLElement;
  const picker = contenedor.querySelector("#picker") as HTMLElement;

  // Estado del picker de items (para DEVOLUCION / FALTANTE).
  let prepara: PreparaPedido | null = null; // catalogo + precios de la lista del cliente
  let preparaClienteId = 0;
  const cant = new Map<number, number>(); // producto_id -> cantidad
  const precioDe = new Map<number, number>();

  // Al entrar se ocultan los articulos. La eleccion dura mientras se permanezca
  // en Cuentas y la impresion usa el mismo contenido que se ve en pantalla.
  let conDetalle = false;
  let lineasPorPedido = new Map<number, LineaDeCuenta[]>();
  let ultimaCarga = 0;

  // Anular desde el estado de cuenta (lo mismo que en la pestana Pedidos): la
  // traba arranca apagada y solo la ve el admin, que es el unico que puede.
  let anulacionHabilitada = false;
  let esAdmin = false;
  try {
    esAdmin = (await api.get<Sesion>("/api/sesion")).es_admin === true;
  } catch {
    esAdmin = false;
  }
  btnHabAnul.classList.toggle("hidden", !esAdmin);

  try {
    const clientes = await api.get<Cliente[]>("/api/clientes?activo=1");
    selCliente.innerHTML =
      `<option value="">Elegir cliente...</option>` +
      clientes.map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`).join("");
  } catch {
    selCliente.innerHTML = `<option value="">Error al cargar clientes</option>`;
  }

  function indexarLineas(lineas: LineaDeCuenta[]): void {
    lineasPorPedido = new Map();
    for (const l of lineas) {
      const arr = lineasPorPedido.get(l.pedido_id) ?? [];
      arr.push(l);
      lineasPorPedido.set(l.pedido_id, arr);
    }
  }

  async function cargar(): Promise<void> {
    const carga = ++ultimaCarga;
    const id = Number(selCliente.value);
    datos = null;
    lineasPorPedido.clear();
    btnDetalle.disabled = true;
    btnImprimir.disabled = true;
    if (!id) {
      salida.innerHTML = "";
      return;
    }
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    try {
      // Se cargan una vez: alternar el detalle no hace nuevas consultas.
      const cuenta = await api.get<CuentaResp>(`/api/clientes/${id}/cuenta?detalle=1`);
      if (carga !== ultimaCarga) return;
      datos = cuenta;
      indexarLineas(datos.lineas ?? []);
      btnDetalle.disabled = false;
      btnImprimir.disabled = false;
      pintar();
    } catch (e) {
      if (carga !== ultimaCarga) return;
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "No se pudo cargar la cuenta")}</div>`;
    }
  }

  /**
   * Las lineas de un pedido, en letra chica debajo de su COMPRA.
   *
   * El bloque llega SOLO hasta la columna Compra (colspan 2, contando Detalle):
   * las columnas Devolucion, Faltante, Pago y Saldo quedan libres. Ocupando
   * todo el ancho el detalle se metia debajo de esas columnas y el papel
   * quedaba sucio; angosto, el texto baja en mas renglones —y si hace falta
   * otra hoja, se hace— pero las columnas de la derecha se leen limpias.
   */
  function detalleDe(mov: CuentaMov): string {
    // Una compra anulada no lleva detalle: el pedido se dio de baja.
    if (!conDetalle || !mov.pedido_id || mov.anulado === 1) return "";
    const lineas = lineasPorPedido.get(mov.pedido_id);
    if (!lineas || lineas.length === 0) return "";
    // Separador entre articulos: en una columna angosta el importe de uno y la
    // cantidad del siguiente quedan pegados y se leen como un solo numero
    // ("$ 50" + "2 Pan flauta" = "$ 502").
    const items = lineas
      .map(
        (l) =>
          `<span class="inline-block whitespace-nowrap"><b>${esc(l.cantidad)}</b> ${esc(l.descripcion)}
             <span class="text-masa-400">${formatearPesos(l.subtotal)}</span></span>`,
      )
      .join(`<span class="px-1.5 text-masa-300">·</span>`);
    return `<tr class="align-top">
      <td></td>
      <td colspan="2" class="pb-1 pl-2 text-[11px] leading-relaxed text-masa-600">${items}</td>
      <td></td><td></td><td></td><td></td>
    </tr>`;
  }

  function pintar(): void {
    if (!datos) return;
    const desde = inpDesde.value;
    const hasta = inpHasta.value;

    // Movimientos ordenados asc con saldo acumulado.
    const movs = [...datos.movimientos].sort((x, y) =>
      x.fecha === y.fecha ? x.id - y.id : x.fecha < y.fecha ? -1 : 1,
    );
    const signo = (t: string) => (t === "COMPRA" ? 1 : -1);
    let acum = 0;
    let saldoAnterior = 0;
    const filas: Array<{ mov: CuentaMov; saldo: number }> = [];
    for (const m of movs) {
      // Un movimiento anulado se sigue mostrando (tachado) pero no mueve el
      // saldo, igual que en la vista v_saldos.
      if (m.anulado !== 1) acum += signo(m.tipo) * m.monto;
      if (desde && m.fecha < desde) { saldoAnterior = acum; continue; }
      if (hasta && m.fecha > hasta) continue;
      filas.push({ mov: m, saldo: acum });
    }

    const tot = { compra: 0, devolucion: 0, faltante: 0, pago: 0 };
    for (const { mov } of filas) {
      if (mov.anulado === 1) continue; // anulado: no cuenta en los totales
      if (mov.tipo === "COMPRA") tot.compra += mov.monto;
      else if (mov.tipo === "DEVOLUCION") tot.devolucion += mov.monto;
      else if (mov.tipo === "FALTANTE") tot.faltante += mov.monto;
      else tot.pago += mov.monto;
    }
    const saldoFinal = filas.length ? filas[filas.length - 1].saldo : saldoAnterior;

    const cli = datos.cliente;
    // Con "Todo" no hay recorte: el saldo al cierre y la deuda total son lo
    // mismo, asi que la aclaracion del periodo sobra.
    const hayPeriodo = Boolean(desde || hasta);
    const periodo = hayPeriodo ? `${ddmmaaaa(desde) || "inicio"} — ${ddmmaaaa(hasta) || "hoy"}` : "Todo el historial";

    const filasHtml = filas
      .map(({ mov, saldo }) => {
        // Pedido anulado: la compra queda a la vista pero tachada y sin saldo,
        // se ve que existio y que no se cobra.
        const anu = mov.anulado === 1;
        const tach = anu ? "text-masa-400 line-through" : "";
        // Con la anulacion habilitada, cada compra que venga de un pedido
        // muestra su boton (no-print: no tiene que salir en la hoja).
        const puedeAnular =
          anulacionHabilitada && !anu && mov.tipo === "COMPRA" && mov.pedido_id !== null;
        return `<tr class="border-t border-crema-100">
          <td class="py-1 pr-2 whitespace-nowrap ${anu ? "text-masa-400" : ""}">${ddmmaaaa(mov.fecha)}</td>
          <td class="py-1 pr-2"><span class="${tach}">${esc(mov.tipo)}${mov.pedido_id ? ` <span class="text-xs text-masa-400">#${mov.pedido_id}</span>` : ""}${mov.notas ? ` <span class="text-xs text-masa-400">${esc(mov.notas)}</span>` : ""}</span>${anu ? ` <span class="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">ANULADO</span>` : ""}${
            puedeAnular
              ? ` <button class="no-print ml-1 rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-100" data-anular-ped="${mov.pedido_id}" data-monto="${mov.monto}" data-fecha="${esc(mov.fecha)}">Anular pedido</button>`
              : ""
          }</td>
          <td class="py-1 pr-2 text-right tabular-nums ${tach}">${mov.tipo === "COMPRA" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums ${tach}">${mov.tipo === "DEVOLUCION" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums ${tach}">${mov.tipo === "FALTANTE" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 pr-2 text-right tabular-nums ${tach}">${mov.tipo === "PAGO" ? formatearPesos(mov.monto) : ""}</td>
          <td class="py-1 text-right font-medium tabular-nums">${anu ? "—" : formatearPesos(saldo)}</td>
        </tr>${detalleDe(mov)}`;
      })
      .join("");

    salida.innerHTML = `
      <div class="tarjeta bg-white print:border-0 print:shadow-none">
        <div class="mb-3 flex items-center justify-between gap-4">
          <img src="${logoUrl}" alt="Torremolinos" class="h-20 w-auto" />
          <div class="text-right text-sm">
            <div class="text-lg font-bold">Estado de cuenta</div>
            <div>${esc(cli.nombre)}</div>
            <div class="text-masa-500">${esc(periodo)}</div>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-crema-100 text-[11px] uppercase tracking-wider text-masa-500"><tr>
              <th class="px-2 py-2 text-left font-semibold">Fecha</th>
              <th class="px-2 py-2 text-left font-semibold">Detalle</th>
              <th class="px-2 py-2 text-right font-semibold">Compra</th>
              <th class="px-2 py-2 text-right font-semibold">Devolucion</th>
              <th class="px-2 py-2 text-right font-semibold">Faltante</th>
              <th class="px-2 py-2 text-right font-semibold">Pago</th>
              <th class="px-2 py-2 text-right font-semibold">Saldo acumulado</th>
            </tr></thead>
            <tbody>
              <tr class="text-masa-500"><td class="px-2 py-1" colspan="6">Saldo antes de este periodo</td><td class="px-2 py-1 text-right tabular-nums">${formatearPesos(saldoAnterior)}</td></tr>
              ${filasHtml || `<tr><td colspan="7" class="px-2 py-4 text-center text-masa-400">Sin movimientos en el periodo.</td></tr>`}
            </tbody>
            <tfoot>
              <tr class="border-t-2 border-masa-300 font-semibold">
                <td class="px-2 py-2" colspan="2">Totales del periodo</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.compra)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.devolucion)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.faltante)}</td>
                <td class="px-2 py-2 text-right tabular-nums">${formatearPesos(tot.pago)}</td>
                <td class="px-2 py-2 text-right tabular-nums">
                  <div class="text-[10px] font-normal uppercase tracking-wider text-masa-400">Saldo al cierre</div>
                  ${formatearPesos(saldoFinal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="mt-3 text-right">
          <span class="text-sm text-masa-500">Deuda total de la cuenta: </span>
          <span class="text-xl font-bold">${formatearPesos(datos.saldo)}</span>
          <p class="mt-1 text-xs text-masa-400">
            Todos los pedidos confirmados menos los pagos, devoluciones y faltantes, tambien los de
            fuera de este periodo.${hayPeriodo ? ` Dentro del periodo elegido el saldo cierra en ${formatearPesos(saldoFinal)}.` : ""}
          </p>
        </div>
      </div>`;
  }

  // ------- Picker de items para DEVOLUCION / FALTANTE (monto calculado) -------
  const porItems = (): boolean => selTipo.value !== "PAGO";

  function recalcularMonto(): void {
    let total = 0;
    for (const [pid, q] of cant) total += q * (precioDe.get(pid) ?? 0);
    total = Math.round(total * 100) / 100;
    inpMonto.value = total ? String(total) : "";
  }

  function pintarPicker(): void {
    if (!prepara) return;
    const porCat = new Map<number, PreparaItem[]>();
    for (const it of prepara.items) {
      if (!(it.precio > 0)) continue;
      const arr = porCat.get(it.producto.categoria_id) ?? [];
      arr.push(it);
      porCat.set(it.producto.categoria_id, arr);
    }
    const nombreCat = new Map(prepara.categorias.map((c) => [c.id, c.nombre]));
    const bloques: string[] = [];
    for (const c of prepara.categorias) {
      const items = porCat.get(c.id);
      if (!items || items.length === 0) continue;
      const cargados = items.reduce((n, it) => n + ((cant.get(it.producto.id) ?? 0) > 0 ? 1 : 0), 0);
      const filas = items
        .map((it) => {
          const pid = it.producto.id;
          const q = cant.get(pid) ?? 0;
          return `<div class="flex items-center gap-2 border-t border-crema-100 py-1.5">
            <div class="flex-1">
              <div class="text-sm text-masa-800">${esc(it.producto.descripcion)}</div>
              <div class="text-xs text-masa-400">${formatearPesos(it.precio)}</div>
            </div>
            <button type="button" class="chip-accion" data-step="-1" data-pid="${pid}" aria-label="Quitar">−</button>
            <input class="campo cant w-20 text-center" type="number" min="0" step="any" inputmode="decimal" data-pid="${pid}" value="${q || ""}" placeholder="0" />
            <button type="button" class="chip-accion" data-step="1" data-pid="${pid}" aria-label="Sumar">+</button>
          </div>`;
        })
        .join("");
      bloques.push(`<details class="rounded-xl border border-crema-200" ${cargados ? "open" : ""}>
        <summary class="cursor-pointer px-3 py-2 text-sm font-semibold text-masa-700">${esc(nombreCat.get(c.id) ?? "Otros")}${cargados ? ` <span class="text-xs font-normal text-masa-400">(${cargados})</span>` : ""}</summary>
        <div class="px-3 pb-2">${filas}</div>
      </details>`);
    }
    picker.innerHTML =
      bloques.join("") ||
      `<div class="tarjeta text-masa-400">El cliente no tiene productos con precio en su lista.</div>`;
  }

  async function cargarPrepara(id: number): Promise<void> {
    picker.innerHTML = `<div class="tarjeta text-masa-400">Cargando articulos...</div>`;
    try {
      prepara = await api.get<PreparaPedido>(`/api/pedidos/preparar?cliente_id=${id}`);
      preparaClienteId = id;
      precioDe.clear();
      for (const it of prepara.items) precioDe.set(it.producto.id, it.precio);
    } catch (e) {
      prepara = null;
      picker.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  async function actualizarModo(): Promise<void> {
    const items = porItems();
    mAyuda.classList.toggle("hidden", !items);
    picker.classList.toggle("hidden", !items);
    inpMonto.readOnly = items;
    inpMonto.classList.toggle("bg-crema-100", items);
    inpMonto.classList.toggle("text-masa-500", items);
    if (!items) {
      inpMonto.value = "";
      return;
    }
    const id = Number(selCliente.value);
    if (!id) {
      picker.innerHTML = `<div class="tarjeta text-masa-400">Elegi un cliente primero.</div>`;
      inpMonto.value = "";
      return;
    }
    if (!prepara || preparaClienteId !== id) await cargarPrepara(id);
    pintarPicker();
    recalcularMonto();
  }

  picker.addEventListener("input", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (!t.classList.contains("cant")) return;
    const pid = Number(t.dataset.pid);
    const v = Math.max(0, Number(t.value.replace(",", ".")) || 0);
    if (v > 0) cant.set(pid, v);
    else cant.delete(pid);
    recalcularMonto();
  });

  picker.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest("[data-step]") as HTMLElement | null;
    if (!b) return;
    const pid = Number(b.dataset.pid);
    const delta = Number(b.dataset.step);
    const nuevo = Math.max(0, (cant.get(pid) ?? 0) + delta);
    if (nuevo > 0) cant.set(pid, nuevo);
    else cant.delete(pid);
    const inp = picker.querySelector(`input.cant[data-pid="${pid}"]`) as HTMLInputElement | null;
    if (inp) inp.value = nuevo ? String(nuevo) : "";
    recalcularMonto();
  });

  selTipo.addEventListener("change", () => void actualizarModo());

  selCliente.addEventListener("change", () => {
    cant.clear();
    prepara = null;
    preparaClienteId = 0;
    void cargar();
    void actualizarModo();
  });
  inpDesde.addEventListener("change", pintar);
  inpHasta.addEventListener("change", pintar);

  contenedor.querySelectorAll<HTMLButtonElement>("[data-rango]").forEach((b) => {
    b.addEventListener("click", () => {
      const tipo = b.dataset.rango as "dia" | "semana" | "mes" | "todo";
      if (tipo === "todo") { inpDesde.value = ""; inpHasta.value = ""; }
      else {
        const ref = inpHasta.value || hoy;
        const [d, h] = rangoFechas(ref, tipo);
        inpDesde.value = d; inpHasta.value = h;
      }
      pintar();
    });
  });

  btnDetalle.addEventListener("click", () => {
    conDetalle = !conDetalle;
    btnDetalle.textContent = conDetalle ? "Sin detalles" : "Con detalles";
    btnDetalle.title = conDetalle ? "Ocultar los articulos de cada compra" : "Mostrar los articulos de cada compra";
    btnDetalle.setAttribute("aria-pressed", String(conDetalle));
    pintar();
  });

  // ------- Anular un pedido desde el estado de cuenta -------
  function confirmarAnulacion(pedidoId: number, fecha: string, monto: number): void {
    const cli = datos?.cliente;
    dlgAnul.innerHTML = `
      <div class="w-[26rem] max-w-full p-5">
        <h2 class="mb-1 text-lg font-bold text-masa-900">Anular el pedido #${pedidoId}?</h2>
        <p class="mb-3 text-sm text-masa-500">Revisa que sea el pedido correcto.</p>
        <div class="mb-4 rounded-xl bg-crema-50 p-3 text-sm">
          <div><b>Cliente:</b> ${esc(cli?.nombre)}</div>
          <div><b>Fecha de la compra:</b> ${ddmmaaaa(fecha)}</div>
          <div><b>Importe:</b> ${formatearPesos(monto)}</div>
        </div>
        <p class="mb-4 text-xs text-masa-500">
          Sale de la produccion de su fecha de entrega y deja de contar en el saldo.
          No se borra: la compra queda tachada en este estado de cuenta.
        </p>
        <p id="ca_err" class="mb-2 text-sm text-red-600"></p>
        <div class="flex justify-end gap-2">
          <button id="ca_cancelar" class="btn-sec">Cancelar</button>
          <button id="ca_ok" class="btn-peligro">${iconoTacho} Anular pedido</button>
        </div>
      </div>`;
    dlgAnul.showModal();
    const err = dlgAnul.querySelector("#ca_err") as HTMLElement;
    const ok = dlgAnul.querySelector("#ca_ok") as HTMLButtonElement;
    dlgAnul.querySelector("#ca_cancelar")?.addEventListener("click", () => dlgAnul.close());
    ok.addEventListener("click", async () => {
      err.textContent = "";
      ok.disabled = true;
      try {
        await api.del(`/api/pedidos/${pedidoId}`);
        dlgAnul.close();
        await cargar(); // recarga la cuenta: la compra ya sale tachada
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo anular";
        ok.disabled = false;
      }
    });
  }

  salida.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest("[data-anular-ped]") as HTMLElement | null;
    if (!b) return;
    confirmarAnulacion(
      Number(b.dataset.anularPed),
      b.dataset.fecha ?? "",
      Number(b.dataset.monto),
    );
  });

  btnHabAnul.addEventListener("click", () => {
    anulacionHabilitada = !anulacionHabilitada;
    btnHabAnul.classList.toggle("armado", anulacionHabilitada);
    btnHabAnul.innerHTML = `${iconoTacho} ${anulacionHabilitada ? "Anulacion habilitada" : "Habilitar anulacion"}`;
    avisoAnul.classList.toggle("hidden", !anulacionHabilitada);
    pintar();
  });

  // La hoja y el PDF salen tal cual se ven, con el modo y los filtros elegidos.
  btnImprimir.addEventListener("click", () => window.print());

  // Registrar PAGO (monto) / DEVOLUCION / FALTANTE (por items, monto calculado)
  const fmov = contenedor.querySelector("#fmov") as HTMLFormElement;
  fmov.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    mErr.textContent = "";
    const id = Number(selCliente.value);
    if (!id) { mErr.textContent = "Elegi un cliente primero"; return; }
    const tipo = selTipo.value;
    const fecha = (contenedor.querySelector("#m_fecha") as HTMLInputElement).value;
    const notas = (contenedor.querySelector("#m_notas") as HTMLInputElement).value.trim() || null;

    let cuerpo: Record<string, unknown>;
    if (porItems()) {
      const items = [...cant.entries()].map(([producto_id, cantidad]) => ({ producto_id, cantidad }));
      if (items.length === 0) { mErr.textContent = "Elegi al menos un articulo"; return; }
      cuerpo = { tipo, items, fecha, notas };
    } else {
      const monto = Number(inpMonto.value);
      if (!(monto > 0)) { mErr.textContent = "Monto invalido"; return; }
      cuerpo = { tipo, monto, fecha, notas };
    }

    try {
      await api.post(`/api/clientes/${id}/cuenta`, cuerpo);
      inpMonto.value = "";
      (contenedor.querySelector("#m_notas") as HTMLInputElement).value = "";
      cant.clear();
      await cargar();
      await actualizarModo();
    } catch (e) {
      mErr.textContent = e instanceof Error ? e.message : "Error";
    }
  });
}
